import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import api, { STORAGE_URL } from '../../api/axios';
import { toast } from 'react-toastify';
import {
  FiGrid, FiSettings, FiPackage, FiTag, FiShoppingCart, FiDroplet,
  FiLogOut, FiMenu, FiX, FiExternalLink, FiGlobe, FiPercent, FiEye,
  FiMapPin, FiHome, FiCreditCard, FiChevronDown, FiPlus, FiShield, FiUsers, FiTruck, FiUserCheck, FiKey, FiMonitor,
} from 'react-icons/fi';
import { dt, getDashLang, setDashLang, DashLang } from '../../utils/dashboardTranslations';
import './DashboardLayout.css';

export default function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [stores, setStores] = useState<any[]>([]);
  const [store, setStore] = useState<any>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState<DashLang>(getDashLang);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const [showAddStore, setShowAddStore] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [addingStore, setAddingStore] = useState(false);
  const [myPerms, setMyPerms] = useState<any>(null); // { is_owner, permissions }

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const res = await api.get('/stores');
      setStores(res.data);
      if (res.data.length > 0) {
        const savedId = localStorage.getItem('cartz_active_store');
        const found = savedId ? res.data.find((s: any) => String(s.id) === savedId) : null;
        setStore(found || res.data[0]);
      }
    } catch {}
  };

  const switchStore = (s: any) => {
    setStore(s);
    localStorage.setItem('cartz_active_store', String(s.id));
    setStoreDropdownOpen(false);
  };

  const handleAddStore = async () => {
    if (!newStoreName.trim()) return;
    setAddingStore(true);
    try {
      const res = await api.post('/stores', { name: newStoreName.trim() });
      setStores((prev) => [...prev, res.data]);
      setStore(res.data);
      localStorage.setItem('cartz_active_store', String(res.data.id));
      setShowAddStore(false);
      setNewStoreName('');
      toast.success(lang === 'ar' ? 'تم إنشاء المتجر بنجاح!' : 'Store created successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create store');
    } finally {
      setAddingStore(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleLang = () => {
    const next = lang === 'en' ? 'ar' : 'en';
    setLang(next);
    setDashLang(next);
    // Sync to backend
    api.put('/user/lang', { preferred_lang: next }).catch(() => {});
  };

  const isRTL = lang === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = isRTL ? 'ar' : 'en';
  }, [isRTL]);

  // Redirect to plan selection if store has no plan
  useEffect(() => {
    if (!store?.id) return;
    if (window.location.pathname.includes('/subscription')) return;
    api.get(`/subscription?store_id=${store.id}`).then(res => {
      if (res.data.needs_plan_selection) {
        navigate('/dashboard/subscription');
      }
    }).catch(() => {});
  }, [store?.id, navigate]);

  // Load user permissions for this store
  useEffect(() => {
    if (!store?.id) return;
    api.get(`/stores/${store.id}/my-permissions`)
      .then(res => setMyPerms(res.data))
      .catch(() => setMyPerms(null));
  }, [store?.id]);

  // Helper: check if user can access a resource
  const canAccess = (resource: string): boolean => {
    if (!myPerms) return true; // still loading, show all
    if (myPerms.is_owner) return true;
    const perms = myPerms.permissions || {};
    const rp = perms[resource];
    if (!rp) return false;
    // Any truthy permission on this resource = can access
    return Object.values(rp).some((v: any) => !!v);
  };

  const allNavItems = [
    { to: '/dashboard', icon: <FiGrid />, label: dt('dashboard', lang), end: true, resource: 'dashboard' },
    { to: '/dashboard/categories', icon: <FiTag />, label: dt('categories', lang), resource: 'categories' },
    { to: '/dashboard/products', icon: <FiPackage />, label: dt('products', lang), resource: 'products' },
    { to: '/dashboard/orders', icon: <FiShoppingCart />, label: dt('orders', lang), resource: 'orders' },
    { to: '/dashboard/pos', icon: <FiMonitor />, label: dt('pos', lang), resource: 'pos', feature: 'pos_enabled' },
    { to: '/dashboard/deliveries', icon: <FiTruck />, label: dt('deliveries', lang), resource: 'deliveries' },
    { to: '/dashboard/branches', icon: <FiHome />, label: dt('branches', lang), resource: 'branches' },
    { to: '/dashboard/delivery-areas', icon: <FiMapPin />, label: dt('delivery_areas', lang), resource: 'delivery_areas' },
    { to: '/dashboard/promo-codes', icon: <FiPercent />, label: dt('promo_codes', lang), resource: 'promo_codes' },
    { to: '/dashboard/customers', icon: <FiUserCheck />, label: dt('customers', lang), resource: 'customers' },
    { to: '/dashboard/payment-gateways', icon: <FiCreditCard />, label: dt('payment_gateways', lang), resource: 'payment_gateways' },
    { to: '/dashboard/design', icon: <FiDroplet />, label: dt('design', lang), resource: 'design' },
    { to: '/dashboard/theme-editor', icon: <FiEye />, label: dt('theme_editor', lang), resource: 'design' },
    { to: '/dashboard/settings', icon: <FiSettings />, label: dt('settings', lang), resource: 'settings' },
    { to: '/dashboard/api-access', icon: <FiKey />, label: dt('api_access', lang), resource: 'api_access' },
    { to: '/dashboard/team', icon: <FiUsers />, label: dt('team', lang), resource: 'team' },
  ];

  const navItems = allNavItems.filter(item => canAccess(item.resource) && (!item.feature || store?.[item.feature]));

  return (
    <div className={`dashboard ${isRTL ? 'dashboard-rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="store-switcher" style={{ flex: 1, position: 'relative' }}>
            <button
              className="store-switcher-btn"
              onClick={() => setStoreDropdownOpen(!storeDropdownOpen)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                {store?.logo ? (
                  <img src={`${STORAGE_URL}/${store.logo}`} alt={store.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <span className="logo-icon">C</span>
                )}
                <div style={{ minWidth: 0, textAlign: isRTL ? 'right' : 'left' }}>
                  <div className="store-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {store?.name || dt('loading', lang)}
                  </div>
                  <div className="store-url">{store?.custom_domain || `${store?.slug}.cartz.cc`}</div>
                </div>
              </div>
              {stores.length > 0 && <FiChevronDown style={{ flexShrink: 0, transition: 'transform 0.2s', transform: storeDropdownOpen ? 'rotate(180deg)' : 'none' }} />}
            </button>

            {storeDropdownOpen && (
              <>
                <div className="store-dropdown-overlay" onClick={() => setStoreDropdownOpen(false)} />
                <div className="store-dropdown">
                  {stores.map((s) => (
                    <button
                      key={s.id}
                      className={`store-dropdown-item ${s.id === store?.id ? 'active' : ''}`}
                      onClick={() => switchStore(s)}
                    >
                      {s.logo ? (
                        <img src={`${STORAGE_URL}/${s.logo}`} alt={s.name} style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />
                      ) : (
                        <span style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', flexShrink: 0 }}>
                          {s.name?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{s.slug}.cartz.cc</div>
                      </div>
                    </button>
                  ))}
                  <button
                    className="store-dropdown-item add-store-btn"
                    onClick={() => { setStoreDropdownOpen(false); setShowAddStore(true); }}
                  >
                    <FiPlus style={{ fontSize: 16 }} />
                    <span>{lang === 'ar' ? 'إضافة متجر جديد' : 'Add New Store'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <FiX />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          {store && (
            <a
              href={store.custom_domain ? `https://${store.custom_domain}` : `https://${store.slug}.cartz.cc`}
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link view-store"
            >
              <FiExternalLink />
              <span>{dt('view_store', lang)}</span>
            </a>
          )}
          {canAccess('subscription') && (
            <NavLink to="/dashboard/subscription" className="sidebar-link subscription-link">
              <FiCreditCard />
              <span>{dt('subscription', lang)}</span>
            </NavLink>
          )}
          {user?.role === 'admin' && (
            <NavLink to="/admin" className="sidebar-link" style={{ color: 'var(--primary)', fontWeight: 600 }}>
              <FiShield />
              <span>Super Admin</span>
            </NavLink>
          )}
          <button className="sidebar-link logout-btn" onClick={handleLogout}>
            <FiLogOut />
            <span>{dt('logout', lang)}</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        {/* Impersonation banner */}
        {localStorage.getItem('impersonating') === 'true' && (
          <div style={{
            background: '#b45309', color: 'white', padding: '8px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: 13, fontWeight: 600,
          }}>
            <span>Viewing as: {user?.first_name} {user?.last_name} ({user?.email})</span>
            <button
              onClick={() => {
                const adminToken = localStorage.getItem('admin_return_token');
                localStorage.removeItem('impersonating');
                localStorage.removeItem('admin_return_user');
                localStorage.removeItem('admin_return_token');
                if (adminToken) {
                  localStorage.setItem('token', adminToken);
                }
                window.location.href = '/admin/stores';
              }}
              style={{
                background: 'white', color: '#92400e', border: 'none', borderRadius: 6,
                padding: '4px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 12,
              }}>
              Return to Admin
            </button>
          </div>
        )}
        <header className="dashboard-header">
          <button className="mobile-sidebar-btn" onClick={() => setSidebarOpen(true)}>
            <FiMenu />
          </button>
          <div className="header-right" style={{ flex: 1, justifyContent: 'flex-end' }}>
            <span className="user-greeting">
              {dt('hello', lang)}, <strong>{user?.first_name}</strong>
            </span>
            <button className="dash-header-logout" onClick={handleLogout} title={dt('logout', lang)}>
              <FiLogOut />
            </button>
            <button className="dash-lang-toggle" onClick={toggleLang} title="Toggle Language">
              <FiGlobe />
              <span>{lang === 'en' ? 'عربي' : 'EN'}</span>
            </button>
          </div>
        </header>
        <div className="dashboard-content">
          <Outlet context={{ store, reloadStore: loadStores, lang, myPerms }} />
        </div>
      </main>

      {/* Add Store Modal */}
      {showAddStore && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !addingStore && setShowAddStore(false)}
        >
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>
              {lang === 'ar' ? 'إضافة متجر جديد' : 'Add New Store'}
            </h2>
            <p style={{ color: 'var(--gray-500)', margin: '0 0 20px', fontSize: 14 }}>
              {lang === 'ar' ? 'أدخل اسم المتجر الجديد' : 'Enter your new store name'}
            </p>
            <input
              type="text"
              value={newStoreName}
              onChange={(e) => setNewStoreName(e.target.value)}
              placeholder={lang === 'ar' ? 'اسم المتجر' : 'Store name'}
              onKeyDown={(e) => e.key === 'Enter' && handleAddStore()}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10,
                border: '2px solid var(--gray-200)', fontSize: 15,
                outline: 'none', marginBottom: 16, boxSizing: 'border-box',
              }}
              autoFocus
            />
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              onClick={handleAddStore}
              disabled={addingStore || !newStoreName.trim()}
            >
              {addingStore
                ? (lang === 'ar' ? 'جاري الإنشاء...' : 'Creating...')
                : (lang === 'ar' ? 'إنشاء المتجر' : 'Create Store')}
            </button>
            <button
              style={{ width: '100%', marginTop: 8, padding: 10, background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer' }}
              onClick={() => setShowAddStore(false)}
              disabled={addingStore}
            >
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
